import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { Enterprise, Impersonator, Role, User } from '@proptr/shared';
import type { Client } from './index.js';
import { ApiError } from './index.js';

interface AuthState {
  user: User | null;
  enterprise: Enterprise | null;
  // Set when the current session is an admin impersonating a manager.
  // Drives the impersonation banner in the manager portal.
  impersonator: Impersonator | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({
  client,
  children,
}: {
  client: Client;
  children: ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [enterprise, setEnterprise] = useState<Enterprise | null>(null);
  const [impersonator, setImpersonator] = useState<Impersonator | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const res = await client.auth.me();
      setUser(res.user);
      setEnterprise(res.enterprise ?? null);
      setImpersonator(res.impersonator ?? null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
        setEnterprise(null);
        setImpersonator(null);
      } else {
        throw err;
      }
    }
  };

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [client]);

  const login: AuthState['login'] = async (email, password) => {
    const res = await client.auth.login({ email, password });
    setUser(res.user);
    setEnterprise(res.enterprise ?? null);
    setImpersonator(res.impersonator ?? null);
    return res.user;
  };

  const logout: AuthState['logout'] = async () => {
    await client.auth.logout();
    setUser(null);
    setEnterprise(null);
    setImpersonator(null);
  };

  return (
    <AuthCtx.Provider
      value={{
        user,
        enterprise,
        impersonator,
        loading,
        login,
        logout,
        refresh,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function RequireAuth({
  role,
  children,
  fallback,
}: {
  role?: Role | Role[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return fallback ?? <p>Not signed in.</p>;
  if (role) {
    const allowed = Array.isArray(role) ? role : [role];
    if (!allowed.includes(user.role)) return fallback ?? <p>Forbidden.</p>;
  }
  return <>{children}</>;
}
